import React, { Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import toast from 'react-hot-toast'
import ModernLayout from './components/layout/ModernLayout'
import Loading, { ContentLoading } from './components/Loading'
import { useAuth } from './hooks/useAuth'
import { usePermissoes } from './hooks/usePermissoes'
import { useVersionCheck } from './hooks/useVersionCheck'

// Pages - Lazy loading para melhor performance
const ModernDashboard = React.lazy(() => import('./pages/dashboard/ModernDashboard'))
const Produtos = React.lazy(() => import('./pages/estoque/Produtos'))
const Grupos = React.lazy(() => import('./pages/estoque/Grupos'))
const Subgrupos = React.lazy(() => import('./pages/estoque/Subgrupos'))
const Categorias = React.lazy(() => import('./pages/estoque/Categorias'))
const Marcas = React.lazy(() => import('./pages/estoque/Marcas'))
const Estoques = React.lazy(() => import('./pages/estoque/Estoques'))
const Transferencias = React.lazy(() => import('./pages/estoque/Transferencias'))
const Ajustes = React.lazy(() => import('./pages/estoque/Ajustes'))
const ExtratoMovimentos = React.lazy(() => import('./pages/estoque/ExtratoMovimentos'))
const CurvaAbc = React.lazy(() => import('./pages/estoque/CurvaAbc'))
const ProdutoFornecedor = React.lazy(() => import('./pages/estoque/ProdutoFornecedor'))
const Pessoas = React.lazy(() => import('./pages/pessoas/Pessoas'))
const OrdensServico = React.lazy(() => import('./pages/ordens-servico/OrdensServico'))
const Contratos = React.lazy(() => import('./pages/contratos/Contratos'))
const ContratosModelos = React.lazy(() => import('./pages/contratos/ContratosModelos'))
const FormasPagamento = React.lazy(() => import('./pages/financeiro/FormasPagamento'))
const CarteiraCobranca = React.lazy(() => import('./pages/financeiro/CarteiraCobranca'))
const Bancos = React.lazy(() => import('./pages/financeiro/Bancos'))
const ContasReceber = React.lazy(() => import('./pages/financeiro/ContasReceber'))
const ContasPagar = React.lazy(() => import('./pages/financeiro/ContasPagar'))
const CaixaDiario = React.lazy(() => import('./pages/financeiro/CaixaDiario'))
const FluxoCaixa = React.lazy(() => import('./pages/financeiro/FluxoCaixa'))
const PlanoContas = React.lazy(() => import('./pages/financeiro/PlanoContas'))
const CentrosCusto = React.lazy(() => import('./pages/financeiro/CentrosCusto'))
const Dre = React.lazy(() => import('./pages/financeiro/Dre'))
const PDV = React.lazy(() => import('./pages/vendas/PDV'))
const CobrancaDisplay = React.lazy(() => import('./pages/financeiro/CobrancaDisplay'))
const Saida = React.lazy(() => import('./pages/movimento/Saida'))
const Empresa = React.lazy(() => import('./pages/configuracoes/Empresa'))
const Usuarios = React.lazy(() => import('./pages/configuracoes/Usuarios'))
const PerfisAcesso = React.lazy(() => import('./pages/configuracoes/PerfisAcesso'))
const Integracoes = React.lazy(() => import('./pages/configuracoes/Integracoes'))
const TerminaisCaixa = React.lazy(() => import('./pages/configuracoes/TerminaisCaixa'))
const NotificacoesCobranca = React.lazy(() => import('./pages/configuracoes/NotificacoesCobranca'))
const Login = React.lazy(() => import('./pages/auth/Login'))
const OSPublica = React.lazy(() => import('./pages/publico/OSPublica'))
const PortalCliente = React.lazy(() => import('./pages/publico/portal/PortalCliente'))
const PortalConvite = React.lazy(() => import('./pages/publico/portal/PortalConvite'))
const PortalLogin = React.lazy(() => import('./pages/publico/portal/PortalLogin'))
const PortalNovaSenha = React.lazy(() => import('./pages/publico/portal/PortalNovaSenha'))

// Serviços (NFS-e)
const Servicos = React.lazy(() => import('./pages/servicos/Servicos'))
const EmitirNfse = React.lazy(() => import('./pages/servicos/EmitirNfse'))

// Ocorrencias
const Ocorrencias = React.lazy(() => import('./pages/ocorrencias/Ocorrencias'))

// Documentos Eletronicos
const DocumentosEletronicos = React.lazy(() => import('./pages/documentos-eletronicos/DocumentosEletronicos'))

// Fiscal
const CodificacaoFiscal = React.lazy(() => import('./pages/fiscal/CodificacaoFiscal'))
const CodificacaoFiscalNcm = React.lazy(() => import('./pages/fiscal/CodificacaoFiscalNcm'))
const TabelaIcms = React.lazy(() => import('./pages/fiscal/TabelaIcms'))
const FiscalDePara = React.lazy(() => import('./pages/fiscal/FiscalDePara'))
const SeriesFiscais = React.lazy(() => import('./pages/fiscal/SeriesFiscais'))
// Placeholder components for missing pages
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="space-y-8">
    <div>
      <h1 className="text-4xl font-bold text-text-primary mb-2">{title}</h1>
      <p className="text-lg text-text-secondary">Página em desenvolvimento</p>
    </div>
    <div className="card-modern">
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">🚧</span>
        </div>
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          Em Desenvolvimento
        </h3>
        <p className="text-text-secondary">
          Esta funcionalidade será implementada na próxima etapa.
        </p>
      </div>
    </div>
  </div>
)

// Componente para proteger rotas
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, usuarioLogado, usuarioCarregado } = useAuth()

  if (loading) {
    return <Loading message="Verificando autenticação..." />
  }

  if (!isAuthenticated) {
    window.location.href = '/login'
    return null
  }

  // A busca em `usuarios` termina depois do `loading`; enquanto ela não voltar,
  // não dá para concluir nada — sair daqui agora derrubaria o usuário no login.
  if (!usuarioCarregado) {
    return <Loading message="Carregando seus dados..." />
  }

  // Estar autenticado não basta: o ERP exige cadastro interno (tabela `usuarios`).
  // Sem isso, um cliente do Portal — que só existe em `portal_usuarios` — entraria
  // aqui, e a policy de `lancamentos` (que não filtra por usuário) exporia dados.
  if (!usuarioLogado) {
    window.location.href = '/login'
    return null
  }

  return <>{children}</>
}

// Componente para verificar permissões de rota
function RouteGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { canAccessRoute, loading } = usePermissoes()

  useEffect(() => {
    if (!loading && !canAccessRoute(location.pathname)) {
      toast.error('Você não tem permissão para acessar esta página')
      navigate('/', { replace: true })
    }
  }, [location.pathname, canAccessRoute, loading, navigate])

  // Enquanto carrega permissões, mostrar loading
  if (loading) {
    return <ContentLoading />
  }

  // Se não tem permissão, não renderizar nada (o useEffect vai redirecionar)
  if (!canAccessRoute(location.pathname)) {
    return null
  }

  return <>{children}</>
}

function App() {
  // Verifica atualizações de versão
  useVersionCheck()

  return (
    <>
      <Router future={{ v7_startTransition: true }}>
        <Suspense fallback={<Loading message="Carregando página..." />}>
          <Routes>
            {/* Rota de Login */}
            <Route path="/login" element={<Login />} />

            {/* Rota pública da OS acessada via QR code (sem auth) */}
            <Route path="/os/:slug" element={<OSPublica />} />

            {/* Portal do Cliente (público, autenticação própria por CPF/CNPJ) */}
            <Route path="/portalcliente" element={<PortalLogin />} />
            <Route path="/portalcliente/nova-senha/:token" element={<PortalNovaSenha />} />
            {/* Área logada separada do convite de propósito: /portal/contratos
                casaria com /portal/:token e cairia na tela de convite */}
            <Route path="/portalcliente/app/*" element={<PortalCliente />} />
            {/* Convite: é o link que já foi enviado aos clientes */}
            <Route path="/portal/:token" element={<PortalConvite />} />

            {/* PDV - Tela inteira sem sidebar */}
            <Route path="/vendas/pdv" element={
              <ProtectedRoute>
                <PDV />
              </ProtectedRoute>
            } />

            {/* Tela externa de cobrança (monitor secundário) */}
            <Route path="/cobranca-display" element={
              <ProtectedRoute>
                <CobrancaDisplay />
              </ProtectedRoute>
            } />

            {/* Redirect legado /vendas -> /movimento/saida */}
            <Route path="/vendas" element={<Navigate to="/movimento/saida" replace />} />

            {/* Rotas Protegidas com Layout */}
            <Route path="/*" element={
              <ProtectedRoute>
                <ModernLayout>
                  <RouteGuard>
                    <Suspense fallback={<ContentLoading />}>
                    <Routes>
                      {/* Dashboard */}
                      <Route path="/" element={<ModernDashboard />} />

                    {/* Estoque */}
                      <Route path="/estoque/produtos" element={<Produtos />} />
                      <Route path="/estoque/grupos" element={<Grupos />} />
                      <Route path="/estoque/subgrupos" element={<Subgrupos />} />
                      <Route path="/estoque/categorias" element={<Categorias />} />
                      <Route path="/estoque/marcas" element={<Marcas />} />
                      <Route path="/estoque/estoques" element={<Estoques />} />
                      <Route path="/estoque/transferencias" element={<Transferencias />} />
                      <Route path="/estoque/ajustes" element={<Ajustes />} />
                      <Route path="/estoque/extrato" element={<ExtratoMovimentos />} />
                      <Route path="/estoque/curva-abc" element={<CurvaAbc />} />
                      <Route path="/estoque/produto-fornecedor" element={<ProdutoFornecedor />} />

                      {/* Pessoas */}
                      <Route path="/pessoas" element={<Pessoas />} />

                      {/* Movimento de Saida (antes /vendas) */}
                      <Route path="/movimento/saida" element={<Saida />} />

                      {/* Ordens de Serviço */}
                      <Route path="/ordens-servico" element={<OrdensServico />} />

                      {/* Contratos */}
                      <Route path="/contratos" element={<Contratos />} />
                      <Route path="/contratos/modelos" element={<ContratosModelos />} />

                      {/* Serviços (NFS-e) */}
                      <Route path="/servicos" element={<Servicos />} />
                      <Route path="/servicos/emitir-nfse" element={<EmitirNfse />} />

                      {/* Financeiro */}
                      <Route path="/financeiro/formas-pagamento" element={<FormasPagamento />} />
                      <Route path="/financeiro/carteira-cobranca" element={<CarteiraCobranca />} />
                      <Route path="/financeiro/bancos" element={<Bancos />} />
                      <Route path="/financeiro/contas-receber" element={<ContasReceber />} />
                      <Route path="/financeiro/contas-pagar" element={<ContasPagar />} />
                      <Route path="/financeiro/caixa-diario" element={<CaixaDiario />} />
                      <Route path="/financeiro/fluxo-caixa" element={<FluxoCaixa />} />
                      <Route path="/financeiro/plano-contas" element={<PlanoContas />} />
                      <Route path="/financeiro/centros-custo" element={<CentrosCusto />} />
                      <Route path="/financeiro/dre" element={<Dre />} />

                      {/* Documentos Eletronicos */}
                      <Route path="/movimento/entrada" element={<DocumentosEletronicos />} />
                      <Route path="/documentos-eletronicos" element={<Navigate to="/movimento/entrada" replace />} />

                      {/* Fiscal */}
                      <Route path="/fiscal/codificacao" element={<CodificacaoFiscal />} />
                      <Route path="/fiscal/codificacao-ncm" element={<CodificacaoFiscalNcm />} />
                      <Route path="/fiscal/tabela-icms" element={<TabelaIcms />} />
                      <Route path="/fiscal/de-para" element={<FiscalDePara />} />
                      <Route path="/fiscal/series" element={<SeriesFiscais />} />

                      {/* Ocorrencias */}
                      <Route path="/ocorrencias" element={<Ocorrencias />} />

                      {/* Configurações */}
                      <Route path="/configuracoes/empresa" element={<Empresa />} />
                      <Route path="/configuracoes/usuarios" element={<Usuarios />} />
                      <Route path="/configuracoes/perfis-acesso" element={<PerfisAcesso />} />
                      <Route path="/configuracoes/integracoes" element={<Integracoes />} />
                      <Route path="/configuracoes/terminais-caixa" element={<TerminaisCaixa />} />
                      <Route path="/configuracoes/notificacoes-cobranca" element={<NotificacoesCobranca />} />
                    </Routes>
                    </Suspense>
                  </RouteGuard>
                </ModernLayout>
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </Router>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            color: '#1f2937',
            fontSize: '14px',
            fontWeight: '500',
            padding: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
        }}
      />
    </>
  )
}

export default App